import * as XLSX from 'xlsx-js-style';
import { Company, WorkTime } from '../types';
import axiosInstance from './axiosInstance';

// Бренд-токени зі специфікації WordCraft (ідентичні до вивантаження Клієнтів):
// бірюза #0a8a88, заголовок #07403f, світлий фон #f3f7f6, текст #5d726f.
const BRAND = '0A8A88';
const HEADER_TEXT = 'FFFFFF';
const ROW_LIGHT = 'F3F7F6';
const ROW_WHITE = 'FFFFFF';
const TEXT_DARK = '07403F';
const TEXT_MUTED = '5D726F';
const BORDER = 'D8E6E4';

const HEADERS = [
  '№',
  'Назва компанії',
  'Код ЄДРПОУ',
  'ПІБ керівника',
  'Посада керівника',
  'Email',
  'Телефон',
  'Тариф',
  'Номер договору',
  'Договір діє з',
  'Договір діє до',
  'Рахунок / IBAN',
  'Вебсторінка',
  'Години роботи',
  'Теги',
];

// Індекси колонок, що переносять довгий текст на кілька рядків.
const WRAP_COLS = new Set([13, 14]);
// Приглушений колір для другорядних колонок (години роботи, теги).
const MUTED_COLS = new Set([13, 14]);

const thin = { style: 'thin', color: { rgb: BORDER } };
const BORDER_ALL = { top: thin, bottom: thin, left: thin, right: thin };

const fmtDate = (v?: string) =>
  v
    ? new Date(v).toLocaleDateString('uk-UA', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '—';

// Години роботи — кожен день окремим записом «Пн 09:00–18:00», через «; »
// (dayOfTheWeek: 1=Пн … 7=Нд, як у картці компанії).
const DAYS_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];
const fmtWorkTime = (wt?: WorkTime[]): string => {
  if (!wt || !wt.length) return '—';
  return wt
    .map((w) => {
      const day = DAYS_SHORT[w.dayOfTheWeek - 1] ?? '?';
      return `${day} ${w.timeStart}–${w.timeEnd}`;
    })
    .join('; ');
};

const headerStyle = () => ({
  fill: { fgColor: { rgb: BRAND } },
  font: { name: 'Nunito', bold: true, sz: 12, color: { rgb: HEADER_TEXT } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border: BORDER_ALL,
});

const bodyStyle = (row: number, col: number) => ({
  fill: { fgColor: { rgb: row % 2 === 0 ? ROW_LIGHT : ROW_WHITE } },
  font: {
    name: 'Nunito',
    sz: 11,
    color: { rgb: MUTED_COLS.has(col) ? TEXT_MUTED : TEXT_DARK },
  },
  alignment: {
    horizontal: col === 0 ? 'center' : 'left',
    vertical: 'center',
    wrapText: WRAP_COLS.has(col),
  },
  border: BORDER_ALL,
});

/**
 * Тягне ВЕСЬ перелік компаній, що відповідає поточному пошуку/сортуванню
 * (в обхід пагінації списку). Компанії використовують 0-based pageIndex.
 */
export const fetchAllCompanies = async (
  orderDirection: string,
  searchString: string,
  orderBy: string,
): Promise<Company[]> => {
  const res = await axiosInstance.post('/Admin/GetCompanyList', {
    pageIndex: 0,
    pageSize: 100000,
    searchString,
    orderDirection,
    orderBy,
  });
  return res.data?.items ?? [];
};

/** Генерує стилізований .xlsx з усіма полями картки компанії й запускає завантаження. */
export const exportCompaniesToExcel = (companies: Company[]) => {
  const rows = companies.map((c, i) => [
    i + 1,
    c.name || '',
    c.edrpou || '',
    c.directorUserName || '',
    c.directorPosition || '',
    c.email || '',
    c.phone || '',
    c.plan || 'Без тарифу',
    c.contractNumber || '',
    fmtDate(c.contractStartAt),
    fmtDate(c.contractFinishAt),
    c.iban || '',
    c.website || '',
    fmtWorkTime(c.workTime),
    (c.tags || []).map((t) => t.name).join(', '),
  ]);

  const ws = XLSX.utils.aoa_to_sheet([HEADERS, ...rows]);
  const range = XLSX.utils.decode_range(ws['!ref'] as string);

  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (!cell) continue;
      cell.s = R === 0 ? headerStyle() : bodyStyle(R, C);
    }
  }

  ws['!cols'] = [
    { wch: 5 }, // №
    { wch: 30 }, // Назва
    { wch: 14 }, // ЄДРПОУ
    { wch: 26 }, // ПІБ керівника
    { wch: 22 }, // Посада
    { wch: 30 }, // Email
    { wch: 16 }, // Телефон
    { wch: 20 }, // Тариф
    { wch: 16 }, // Номер договору
    { wch: 14 }, // Договір з
    { wch: 14 }, // Договір до
    { wch: 26 }, // IBAN
    { wch: 24 }, // Вебсторінка
    { wch: 34 }, // Години роботи
    { wch: 28 }, // Теги
  ];
  ws['!rows'] = [{ hpt: 32 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Компанії');

  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `WordCraft_Компанії_${stamp}.xlsx`);
};
